// Écran OD Salaires : formulaire de paie guidé.
// L'utilisateur saisit des termes de paie ; Porinetia Compta AERH calcule le net
// à payer et le montant à reverser, puis génère l'opération équilibrée.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatErreur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DatePicker from "@/components/DatePicker";
import JournalEcritures from "@/components/JournalEcritures";
import ApercuEcritures from "@/components/ApercuEcritures";
import { Loader2, Users2 } from "lucide-react";

const fmt = (n) => `${Math.round(n || 0).toLocaleString("fr-FR")} XPF`;
const aujourdHui = () => new Date().toISOString().slice(0, 10);

export default function ODSalaires() {
  const [dateOperation, setDateOperation] = useState(aujourdHui());
  const [periode, setPeriode] = useState("");
  const [brut, setBrut] = useState("");
  const [patronales, setPatronales] = useState("");
  const [salariales, setSalariales] = useState("");
  const [enregistrement, setEnregistrement] = useState(false);
  const [operations, setOperations] = useState([]);
  const [numero, setNumero] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const charger = useCallback(async () => {
    try {
      const [o, n] = await Promise.all([
        api.get("/operations", { params: { type: "od_salaires" } }),
        api.get("/operations/prochain-numero", { params: { type: "od_salaires" } }),
      ]);
      setOperations(o.data || []);
      setNumero(n.data?.numero || "");
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const brutN = Math.round(parseFloat(brut) || 0);
  const patN = Math.round(parseFloat(patronales) || 0);
  const salN = Math.round(parseFloat(salariales) || 0);
  const net = Math.max(brutN - salN, 0);
  const cps = salN + patN;
  const cotisationsExcessives = salN > brutN && brutN > 0;

  // Écritures générées (Débit/Crédit) : 641100 brut + 645100 patronales au débit ;
  // 431000 CPS + 421000 net au crédit.
  const apercuLignes = [
    { compte: "641100", libelle: "Salaires", debit: brutN, credit: 0 },
    { compte: "645100", libelle: "Charges patronales", debit: patN, credit: 0 },
    { compte: "431000", libelle: "CPS (à reverser)", debit: 0, credit: cps },
    { compte: "421000", libelle: "Net à payer", debit: 0, credit: net },
  ];

  const reinitialiser = () => {
    setDateOperation(aujourdHui());
    setPeriode("");
    setBrut(""); setPatronales(""); setSalariales("");
  };

  const enregistrer = async () => {
    if (!periode.trim()) { toast.error("Veuillez indiquer la période (ex. Juin 2026)."); return; }
    if (!(brutN > 0)) { toast.error("Le salaire brut doit être supérieur à zéro."); return; }
    if (salN > brutN) { toast.error("Les cotisations salariales ne peuvent pas dépasser le salaire brut."); return; }

    setEnregistrement(true);
    try {
      await api.post("/operations/od-salaires", {
        date_operation: dateOperation, periode,
        salaire_brut: brutN, charges_patronales: patN, cotisations_salariales: salN,
      });
      toast.success("Salaires enregistrés avec succès.");
      reinitialiser();
      charger();
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(formatErreur(e.response?.data?.detail));
    } finally {
      setEnregistrement(false);
    }
  };

  return (
    <div className="fenua-fade-up" data-testid="page-od-salaires">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-lg bg-[#E8F4F4] flex items-center justify-center">
          <Users2 className="h-6 w-6 text-[#1D3D3D]" />
        </div>
        <div>
          <h1 data-testid="titre-section" className="text-3xl font-black tracking-tight text-[#1D3D3D]">ODS — OD Salaires</h1>
          <p className="text-sm text-[#1D3D3D]/70">Enregistrez la paie du mois en quelques champs.</p>
        </div>
      </div>

      <div className="mt-6">
        <JournalEcritures code="ODS" libelleJournal="ODS — OD Salaires" refreshKey={refreshKey} />
      </div>

      <div className="mt-6 grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 bg-white border border-[#4A8A8A]/20 rounded-lg shadow-sm p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label className="text-[#1D3D3D] font-semibold text-sm">Période</Label>
              <Input data-testid="salaires-periode" value={periode} onChange={(e) => setPeriode(e.target.value)}
                placeholder="Ex. Juin 2026" className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#1D3D3D] font-semibold text-sm">Date</Label>
              <DatePicker testId="salaires-date" value={dateOperation} onChange={setDateOperation} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[#1D3D3D] font-semibold text-sm">Salaires bruts</Label>
            <Input type="number" step="1" min="1" inputMode="numeric" data-testid="salaires-brut" value={brut}
              onChange={(e) => setBrut(e.target.value)} placeholder="0"
              className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label className="text-[#1D3D3D] font-semibold text-sm">Cotisations salariales (part salarié)</Label>
              <Input type="number" step="1" min="0" inputMode="numeric" data-testid="salaires-salariales" value={salariales}
                onChange={(e) => setSalariales(e.target.value)} placeholder="0"
                className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#1D3D3D] font-semibold text-sm">Charges patronales (employeur)</Label>
              <Input type="number" step="1" min="0" inputMode="numeric" data-testid="salaires-patronales" value={patronales}
                onChange={(e) => setPatronales(e.target.value)} placeholder="0"
                className="bg-white border-[#4A8A8A]/30 focus-visible:ring-[#4A8A8A]" />
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-[#4A8A8A]/20 rounded-lg shadow-sm p-6">
            <h3 className="font-bold text-[#1D3D3D] mb-4">Récapitulatif</h3>
            <div className="text-xs text-[#1D3D3D]/60 font-mono mb-3">{numero}</div>
            {cotisationsExcessives && (
              <p data-testid="salaires-erreur-cotisations" className="mb-3 text-sm text-red-600">
                Les cotisations salariales ne peuvent pas dépasser le salaire brut.
              </p>
            )}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-[#1D3D3D]/70">Salaires bruts</span><span className="font-medium text-[#1D3D3D]">{fmt(brutN)}</span></div>
              <div className="flex justify-between"><span className="text-[#1D3D3D]/70">Net à payer au salarié</span><span data-testid="salaires-net" className="font-bold text-[#1D3D3D]">{fmt(net)}</span></div>
              <div className="flex justify-between"><span className="text-[#1D3D3D]/70">À reverser (CPS)</span><span data-testid="salaires-cps" className="font-medium text-[#1D3D3D]">{fmt(cps)}</span></div>
            </div>
            <Button data-testid="bouton-enregistrer-salaires" onClick={enregistrer} disabled={enregistrement}
              className="w-full mt-4 bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white font-semibold h-11">
              {enregistrement ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer les salaires"}
            </Button>
          </div>

          <ApercuEcritures lignes={apercuLignes} />

          <div className="bg-white border border-[#4A8A8A]/20 rounded-lg shadow-sm p-6">
            <h3 className="font-bold text-[#1D3D3D] mb-4">Salaires récents</h3>
            {operations.length === 0 ? (
              <p className="text-sm text-[#1D3D3D]/60">Aucun enregistrement pour le moment.</p>
            ) : (
              <div className="space-y-2" data-testid="liste-salaires">
                {operations.slice(0, 8).map((o) => (
                  <div key={o.id} data-testid={`salaire-item-${o.id}`} className="flex items-center justify-between text-sm py-2 border-b border-[#4A8A8A]/10 last:border-0">
                    <div className="min-w-0">
                      <p className="font-mono text-[#1D3D3D] font-medium">{o.numero}</p>
                      <p className="text-[#1D3D3D]/60 text-xs truncate">{o.libelle}</p>
                    </div>
                    <span className="font-semibold text-[#1D3D3D]">{fmt(o.details?.net_a_payer || 0)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
